import { ApiService } from '../apiService'
import { tokenStorage } from '@services/tokenStorage'
import type {
  PresenceActivity,
  PresenceHeartbeatResponse,
  UserPresenceState,
} from '../types/presence'
import type { UserPrivacySettingsDto } from '../../types/settings'
import type { PresenceState } from '../types/presence'

/**
 * Presence over the API.
 *
 * The heartbeat both reports and reads, so presence for the whole app costs one request per
 * beat however many components want it.
 */
export const presenceApi = {
  /** Report activity and get back the presence of the users being watched. */
  heartbeat: (activity: PresenceActivity, watch: string[]) =>
    ApiService.post<PresenceHeartbeatResponse>('/api/me/presence/heartbeat', { activity, watch }),

  /** Immediate read, so an opening conversation is not blank until the next beat. */
  query: (userIds: string[]) =>
    ApiService.post<UserPresenceState[]>('/api/me/presence/query', { userIds }),

  /** The account-menu status toggle. Offline means "appear offline". */
  /**
   * Sign off on purpose, while the session is still alive. The unload variant below cannot be
   * used for this: on sign-out the token is gone by the time the page unloads.
   */
  goOffline: () => ApiService.post<void>('/api/me/presence/offline'),

  setStatus: (status: PresenceState) =>
    ApiService.put<UserPrivacySettingsDto>('/api/me/settings/privacy/presence', { status }),

  /**
   * Sign off during page unload.
   *
   * A plain request is abandoned when the page goes away, so this uses `keepalive`, which lets
   * the browser finish it after the document is gone.
   *
   * Deliberately NOT `navigator.sendBeacon`: beacons cannot carry an Authorization header, and
   * this API is bearer-token based, so a beacon would arrive unauthenticated and be rejected —
   * silently, during unload, where nobody would ever see it fail.
   *
   * Best-effort by nature. A crash or a force-quit sends nothing, which is what the server's
   * staleness window is for; do not try to close that gap here.
   */
  signOffOnUnload: (): void => {
    const token = tokenStorage.getAccessToken()
    if (!token) return

    try {
      void fetch('https://api.hadv.quest/api/me/presence/offline', {
        method: 'POST',
        keepalive: true,
        headers: { Authorization: `Bearer ${token}` },
      })
    } catch {
      // Unload is not a place to handle errors.
    }
  },
}
